import { useEffect, useState } from 'react';

import { CheckmarkBadge03Icon, ShieldEnergyIcon } from '@hugeicons/core-free-icons';
import { CheckmarkBadge02Icon, SecurityCheckIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { darkenHex } from 'hooks/color-contrast-checker';
import { Tab } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { Badge } from 'components/Badge/Badge';
import { Loading } from 'components/Loading/Loading';
import SliderMotion from 'components/Motion/SliderMotion';
import { ProgressBar } from 'components/ProgressBar/ProgressBar';
import { OverflowTabs } from 'components/Tabs/Tabs';

import { useGetConsentFilesQuery, useGetDashboardQuery } from 'api/candidate-portal-api';
import { selectLinkedCandidates, selectPortalAuth } from 'api/candidate-portal-slice';

import { CandidateCheckslist } from './CandidateCheckslist';
import { CandidateConsentList } from './CandidateConsentList';
import styles from './CandidatePortalDashboard.module.scss';
import CandidatePortalPage from './CandidatePortalPage/CandidatePortalPage';

const CandidatePortalDashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, checked } = useSelector(selectPortalAuth);
  const linkedCandidates = useSelector(selectLinkedCandidates);

  const { candidateId } = useParams();

  const {
    data: dashboard,
    isLoading,
    error,
    refetch: refetchDashboard,
  } = useGetDashboardQuery(undefined, {
    skip: !checked || !isAuthenticated || !candidateId,
  });

  const {
    data: consentFiles,
    isLoading: isLoadingConsentFiles,
    error: errorConsentFiles,
    refetch: refetchConsentFiles,
  } = useGetConsentFilesQuery(undefined, {
    skip: !checked || !isAuthenticated || !candidateId,
  });

  const [checks, setChecks] = useState(dashboard?.checks || []);

  useEffect(() => {
    if (dashboard) {
      setChecks(dashboard.checks);
    }
  }, [dashboard]);

  // Handle context selection redirect (authentication is handled by CandidatePortalPage)
  useEffect(() => {
    if (!checked || !isAuthenticated) return; // Wait for auth check to complete

    if (!candidateId && linkedCandidates.length > 1) {
      navigate('/portal/select');
    }
  }, [checked, isAuthenticated, linkedCandidates, navigate, candidateId]);

  const completedChecks =
    checks?.filter(check => check.status === 'completed' && !check.canceled)?.length || 0;

  const activeChecks = checks?.filter(check => !check.canceled)?.length || 0;

  const addOpacityToHex = (hex, opacity) => {
    if (!hex) return '';
    let normalizedHex = hex.replace('#', '');
    if (normalizedHex.length === 3) {
      normalizedHex = normalizedHex
        .split('')
        .map(char => char + char)
        .join('');
    }
    return `#${normalizedHex}${opacity}`;
  };

  const brandStyle = {};
  if (dashboard?.brand?.color_button) {
    brandStyle['--brand-text'] = darkenHex(dashboard.brand.color_button, 40);
    brandStyle['--brand-badge-bg'] = addOpacityToHex(dashboard.brand.color_button, '30');
    brandStyle['--brand-link-color'] = dashboard.brand.color_link;
    brandStyle['--brand-button-bg'] = dashboard.brand.color_button;
    brandStyle['--brand-button-text'] = dashboard.brand.color_button_text;
    brandStyle['--brand-button-text-light'] = addOpacityToHex(
      dashboard.brand.color_button_text,
      '50',
    );
  }

  const bannerClassName = dashboard?.brand?.color_button
    ? `bg-[var(--brand-button-bg)] text-[var(--brand-button-text)]`
    : '';
  const bannerTextClass = dashboard?.brand?.color_button_text
    ? `text-[var(--brand-button-text)]`
    : 'text-white';
  const bannerIconClass = dashboard?.brand?.color_button_text
    ? `text-[var(--brand-button-text-light)]`
    : 'text-white/30';

  if (isLoading) {
    return (
      <CandidatePortalPage showFooter={false}>
        <Loading active />
      </CandidatePortalPage>
    );
  }

  if (error) {
    console.error(error);
    return (
      <CandidatePortalPage candidate={dashboard?.candidate}>
        <div
          className="flex flex-col items-center justify-center min-h-[calc(80vh-7rem)] w-full px-4 gap-8"
          role="alert">
          <div className="d-flex align-items-center justify-content-center">
            <img
              src="assets/images/404.png"
              alt="Application submitted illustration"
              className="u-width-50 u-border-radius--half"
            />
          </div>
          {error?.data?.error?.log ? (
            <div className="p-8 bg-red-1 border border-red-2 rounded-3xl">
              <p className="text-xl text-text-secondary font-regular max-w-xl">
                {error.data.error.log}
              </p>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 bg-red-1 border border-red-2 rounded-3xl">
              <h2 className="text-2xl font-semibold text-text-primary text-center">
                {t('candidatePortal.somethingWentWrong')}
              </h2>
              <p className="text-xl text-text-secondary font-regular text-center max-w-xl mt-2">
                {t('candidatePortal.unableToLoadDashboard')}
              </p>
            </div>
          )}
        </div>
      </CandidatePortalPage>
    );
  }

  return (
    <CandidatePortalPage candidate={dashboard?.candidate} brand={dashboard?.brand}>
      <div className="container px-4 py-8 flex flex-col gap-16">
        {/* Welcome Section */}
        <SliderMotion
          key={`dashboard-${dashboard?.candidate?.id}-welcome`}
          duration={0.5}
          delay={0.1}>
          <section className="flex flex-col gap-8" aria-labelledby="welcome-heading">
            <div className="flex gap-4 items-center">
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-8">
                  <h1 id="welcome-heading" className="text-5xl font-medium text-text-primary">
                    {t('candidatePortal.hello', { name: dashboard?.candidate?.first_name })}
                  </h1>
                </div>
                <div className="flex gap-2">
                  {dashboard?.candidate?.job_role && (
                    <>
                      <span className="text-text-secondary font-regular text-xl">
                        {dashboard?.candidate?.job_role}
                      </span>
                      <span className="text-text-tertiary font-light text-xl" aria-hidden="true">
                        at
                      </span>
                    </>
                  )}
                  <span className="text-text-secondary font-regular text-xl">
                    {dashboard?.company?.name}
                  </span>
                </div>
              </div>
            </div>
            <p className="text-2xl text-text-secondary font-regular">
              {t('candidatePortal.trackYourBackgroundCheckProgressAndAccessYourDocuments')}
            </p>
          </section>
        </SliderMotion>

        <SliderMotion
          key={`dashboard-${dashboard?.candidate?.id}-progress`}
          duration={0.5}
          delay={0.25}>
          <section
            style={brandStyle}
            className={cn(
              'shadow-sm shadow-soft border-0 border border-border-primary rounded-3xl',
              completedChecks === activeChecks ? 'bg-success-gradient' : `${bannerClassName}`,
            )}
            aria-labelledby="progress-heading"
            role="region">
            <article className="flex flex-col gap-8 px-8 py-12">
              <div className="flex flex-row items-center justify-between gap-4">
                <div className="flex flex-col gap-2">
                  <h2
                    id="progress-heading"
                    className={`text-xl font-light m-0 ${completedChecks === activeChecks ? 'text-white' : bannerTextClass}`}>
                    {t('candidatePortal.overallProgress')}
                  </h2>
                  <p
                    className={`text-5xl font-semibold m-0 ${completedChecks === activeChecks ? 'text-white' : bannerTextClass}`}
                    role="status"
                    aria-live="polite">
                    {completedChecks === activeChecks
                      ? t('candidatePortal.backgroundCheckCompleted')
                      : t('candidatePortal.checksCompleted', {
                          completed: completedChecks,
                          total: activeChecks,
                        })}
                  </p>
                </div>
                <div className="flex items-center gap-3" aria-hidden="true">
                  <HugeiconsIcon
                    icon={
                      completedChecks === activeChecks ? CheckmarkBadge03Icon : ShieldEnergyIcon
                    }
                    size="64"
                    className={completedChecks === activeChecks ? 'text-white/40' : bannerIconClass}
                    aria-hidden="true"
                  />
                </div>
              </div>
              {completedChecks !== activeChecks && (
                <div
                  role="progressbar"
                  aria-valuenow={completedChecks}
                  aria-valuemin={0}
                  aria-valuemax={activeChecks}
                  aria-label={t('candidatePortal.progressBar', {
                    completed: completedChecks,
                    total: activeChecks,
                  })}>
                  <ProgressBar
                    current={completedChecks}
                    total={activeChecks}
                    showLabel={false}
                    showPercentage={true}
                    size="medium"
                    variant={completedChecks === activeChecks ? 'success' : 'primary'}
                    animated={true}
                  />
                </div>
              )}
            </article>
          </section>
        </SliderMotion>

        <SliderMotion
          key={`dashboard-${dashboard?.candidate?.id}-checks`}
          duration={0.5}
          delay={0.4}
          className="tab-container">
          <div className={styles.tabsContainer} style={brandStyle}>
            <OverflowTabs
              defaultActiveKey="checks"
              className="sm:mx-0 mx-auto"
              mountOnEnter={true}
              unmountOnExit={true}>
              <Tab
                eventKey="checks"
                title={
                  <span className="flex items-center gap-2">
                    <HugeiconsIcon
                      icon={SecurityCheckIcon}
                      size="16"
                      strokeWidth={2}
                      aria-hidden="true"
                    />{' '}
                    <span>{t('candidatePortal.checks')}</span>
                    {activeChecks > 0 && (
                      <Badge
                        text={activeChecks}
                        className="ml-2 brand-badge"
                        aria-label={t('candidatePortal.activeChecksCount', { count: activeChecks })}
                      />
                    )}
                  </span>
                }>
                <CandidateCheckslist
                  candidate={dashboard?.candidate}
                  company={dashboard?.company}
                  checks={checks}
                  updateChecks={setChecks}
                  style={brandStyle}
                  brand={dashboard?.brand}
                />
              </Tab>
              <Tab
                eventKey="consents"
                title={
                  <span className="flex items-center gap-2">
                    <HugeiconsIcon
                      icon={CheckmarkBadge02Icon}
                      size="16"
                      strokeWidth={2}
                      aria-hidden="true"
                    />{' '}
                    {t('candidatePortal.consents')}
                  </span>
                }>
                <CandidateConsentList
                  candidateId={dashboard?.candidate?.id}
                  consentFiles={consentFiles}
                  isLoading={isLoadingConsentFiles}
                  error={errorConsentFiles}
                  brand={dashboard?.brand}
                />
              </Tab>
            </OverflowTabs>
          </div>
        </SliderMotion>
      </div>
    </CandidatePortalPage>
  );
};

export default CandidatePortalDashboard;
